import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import RetryNotice from './RetryNotice';
import { departmentLabel } from './departments';
import WarningLoading from './WarningLoading';
import apiRequest from '../../api/request';
import WarningMetricTable from './WarningMetricTable';
import FlagSummary from './FlagSummary';
import ThresholdNote from './ThresholdNote';
import useConfigAccess from './useConfigAccess';
import useTutorReplacementDrilldown from './useTutorReplacementDrilldown';
import { REASON_LABEL } from './tutorReplacements';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

// The two cards share everything except who the rows are.
export const MODES = {
  cro: {
    title: 'Tutor Replacement Count',
    endpoint: 'warning-dashboard/tutor-replacement-count-dashboard',
    route: 'tutor-replacement-count',
    configPath: 'tutor-replacement-count-config',
    who: 'CRO',
    people: 'CROs',
    exportName: 'Tutor_Replacement_Count',
    emptyMessage: 'No tutor replacements this period.',
    sentence: (n) => `${n} ${n === 1 ? 'replacement' : 'replacements'}`,
    thresholdText: (value) => `> ${Number(value).toLocaleString('en-IN')}`,
  },
  tutor: {
    title: 'Tutor Replacement Count (by Tutor)',
    endpoint: 'warning-dashboard/tutor-replacement-by-tutor-dashboard',
    route: 'tutor-replacement-by-tutor',
    configPath: 'tutor-replacement-by-tutor-config',
    who: 'Tutor',
    people: 'tutors',
    exportName: 'Tutor_Replacement_Count_By_Tutor',
    emptyMessage: 'No tutors were replaced this period.',
    sentence: (n) => `${n} ${n === 1 ? 'replacement' : 'replacements'}`,
    thresholdText: (value) => `> ${Number(value).toLocaleString('en-IN')}`,
  },
};

const StatusCell = ({ isWarning }) =>
  isWarning ? (
    <WarningAmberIcon fontSize="small" sx={{ color: 'error.main', display: 'block', mx: 'auto' }} />
  ) : (
    <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', display: 'block', mx: 'auto' }} />
  );

// Tutor Replacement Count. A replacement is a tutor assignment that follows an earlier one on the same subject (the earlier tutor was
// unmapped) - the very pairing of the Tutor Replacements register. One row per CRO (how many of their students' tutors were replaced
// in the month) or per replaced tutor (how many times); each count opens the replacements behind it. ONE company-wide threshold per
// page (TutorReplacementConfig.jsx): a row with MORE than it is flagged. The three reason columns only split the count.
const TutorReplacementPage = ({ mode }) => {
  const cfg = MODES[mode];
  // The month is shared across the whole dashboard and remembered for the session (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [thresholdNote, setThresholdNote] = useState(null); // why there is no threshold for this month, when there isn't
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0); // Retry re-runs just this request
  const drill = useTutorReplacementDrilldown(month, mode);
  const { canConfigure } = useConfigAccess();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest({ url: `${cfg.endpoint}?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (cancelled) return;
        // apiRequest resolves even on a failed call (src/api/request.js) - without this check a server error rendered as an empty
        // table saying nothing was replaced, which is false
        if (res?.status !== 'success') {
          setRows([]);
          setSummary(null);
          setError(res?.message || `Could not load ${cfg.title}`);
          return;
        }
        setRows(res?.data?.rows || []);
        setSummary({ totalCount: res?.data?.totalCount ?? 0, people: res?.data?.totalEvaluated ?? 0, warningCount: res?.data?.warningCount ?? 0 });
        setThresholdNote(res?.data?.thresholdNote || null);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || `Could not load ${cfg.title}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, reloadKey, cfg.endpoint, cfg.title]);

  // flagged first, the most replacements at the top
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (Number(b.isWarning) - Number(a.isWarning)) || (b.replacementCount - a.replacementCount)),
    [rows]
  );

  const thresholdOf = (row) => (row.threshold === null ? (row.thresholdOff ? 'Switched off' : 'Not configured') : cfg.thresholdText(row.threshold));

  const columns = [
    { field: 'status', headerName: 'Status', align: 'center', render: (row) => <StatusCell isWarning={row.isWarning} />, csvValue: (row) => (row.isWarning ? 'Warning' : 'OK') },
    {
      field: 'entityName',
      headerName: cfg.who,
      render: (row) => (
        <Typography variant="body2" fontStyle={row.isActive === false ? 'italic' : 'normal'} color={row.isActive === false || row.isWarning ? 'error.main' : 'text.primary'}>
          {row.entityName}{row.isActive === false && ' (Inactive)'}
        </Typography>
      ),
    },
    ...(mode === 'cro'
      ? [{ field: 'departmentId', headerName: 'Department', render: (row) => departmentName(row.departmentId), csvValue: (row) => departmentName(row.departmentId) }]
      : []),
    {
      field: 'replacementCount',
      headerName: 'Replacements',
      align: 'right',
      render: drill.cell('replacementCount'),
      csvValue: (row) => row.replacementCount,
    },
    { field: 'tutor', headerName: REASON_LABEL.tutor, align: 'right', render: drill.cell('tutor', 'tutor'), csvValue: (row) => row.tutor },
    { field: 'parent', headerName: REASON_LABEL.parent, align: 'right', render: drill.cell('parent', 'parent'), csvValue: (row) => row.parent },
    { field: 'other', headerName: REASON_LABEL.other, align: 'right', render: drill.cell('other', 'other'), csvValue: (row) => row.other },
    {
      field: 'threshold',
      headerName: 'Threshold',
      align: 'right',
      render: (row) => (row.threshold === null
        ? <Typography variant="body2" color="text.secondary">{thresholdOf(row)}</Typography>
        : thresholdOf(row)),
      csvValue: thresholdOf,
    },
  ];

  const sum = (shown, field) => shown.reduce((t, r) => t + Number(r[field] || 0), 0).toLocaleString('en-IN');

  return (
    <WarningPage
      title={cfg.title}
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard?month=${month.format('YYYY-MM-DD')}`} variant="text">
            Back to Warning Dashboard
          </Button>
          {canConfigure(cfg.configPath) && (
            <Button component={Link} to={`/dashboard/warning-dashboard/${cfg.configPath}?month=${month.format('YYYY-MM-DD')}`} variant="outlined">
              Configure Threshold
            </Button>
          )}
        </Box>
      }
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker views={['year', 'month']} label="Month" value={month} onChange={(v) => v && setMonth(v)} />
        </LocalizationProvider>
      </Box>

      {loading && <WarningLoading />}

      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && summary && (
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          <strong>{Number(summary.totalCount).toLocaleString('en-IN')}</strong> {summary.totalCount === 1 ? 'tutor replacement' : 'tutor replacements'} by {summary.people} {summary.people === 1 ? cfg.who.toLowerCase() : cfg.people}
        </Typography>
      )}

      {!loading && !error && summary && <FlagSummary warningCount={summary.warningCount} totalEvaluated={summary.people} unit={` ${cfg.people}`} />}
      {!loading && !error && <ThresholdNote note={thresholdNote} month={month} configPath={cfg.configPath} canSet={canConfigure(cfg.configPath)} />}

      {!loading && !error && (
        <WarningMetricTable
          rows={sortedRows}
          columns={columns}
          departmentName={departmentName}
          emptyMessage={cfg.emptyMessage}
          rowKey={(row) => row.entityId}
          footer={(shown) => [
            {
              key: 'total',
              first: true,
              emphasis: true,
              cells: {
                entityName: shown.length === rows.length ? 'Total' : 'Total of the rows shown',
                replacementCount: sum(shown, 'replacementCount'),
                tutor: sum(shown, 'tutor'),
                parent: sum(shown, 'parent'),
                other: sum(shown, 'other'),
              },
            },
          ]}
          exportFileName={cfg.exportName}
        />
      )}
      {drill.dialog}
    </WarningPage>
  );
};

export default TutorReplacementPage;
